import { useState } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';
import gettext from '@sdk/lang/gettext';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import { extendBillingInfo } from '@sdk/account/utils';
import useToast from '@sdk/common/toast/useToast';
import { SUBSCRIPTION_LEVELS } from '@sdk/account/constants';
import usePromiseModalState from '@sdk/components/ui/modal/usePromiseModalState';
import { differenceInDays } from 'date-fns';

import { CANCEL_REASON_SEASONAL, CANCEL_REASON_TOO_EXPENSIVE } from '@web/account/constants';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import { useStripeUtils } from '@web/account/hooks/stripeUtils.hooks';

import config from '@angular/config';

const useDowngradeSubscription = (closeModal) => {
  const api = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();
  const { billingInfo, onBillingInfoUpdated } = useBillingInfo();
  const { authenticateCard } = useStripeUtils();

  const [selectedReasonId, setSelectedReasonId] = useState<number>();
  const [selectedReasonText, setSelectedReasonText] = useState('');
  const [reasonRequiresText, setReasonRequiresText] = useState<boolean>();
  const [isDowngradeOnly, setIsDowngradeOnly] = useState(false);
  const [showFeaturesToLose, setShowFeaturesToLose] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [is50PercentDismissed, setIs50PercentDismissed] = useState(false);
  const [isPauseOfferDismissed, setIsPauseOfferDismissed] = useState(false);
  const didPurchaseOver30DaysAgo =
    billingInfo.subscriptionStartDate &&
    differenceInDays(Date.now(), billingInfo.subscriptionStartDate) >= 30;

  const grant50PercentModalState = usePromiseModalState();
  const downgradeOfferModalState = usePromiseModalState();
  const pauseSubscriptionModalState = usePromiseModalState();
  const creditCardModalState = usePromiseModalState();

  const [terminationOption, setTerminationOption] = useState(
    billingInfo.isOnTrial ? 'immediately' : 'at-period-end'
  );

  const downgradeOrRepurchase = async () => {
    if (billingInfo.subscriptionLevel) {
      const { data } = await api.account.downgradeSubscription({
        reasonId: selectedReasonId,
        reasonText: selectedReasonText,
      });

      return data;
    }

    const newPaymentMethodId = await creditCardModalState.getResult();
    const purchasePayload = {
      subscriptionLevel: SUBSCRIPTION_LEVELS.PREMIUM,
      isYearlyPlan: billingInfo.formerIsYearlyPlan,
    };

    if (newPaymentMethodId) {
      Object.assign(purchasePayload, {
        newPaymentMethodId,
      });
    }

    const { data } = await api.account.purchaseSubscription(purchasePayload);
    await authenticateCard(data);

    showSuccessToast(gettext('Your subscription has been reactivated'));
    return data;
  };

  const onDowngradeSubscription = async () => {
    try {
      const data = await downgradeOrRepurchase();
      onBillingInfoUpdated(extendBillingInfo(data));
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      throw e;
    }
  };

  const limitChurn = async () => {
    if (!didPurchaseOver30DaysAgo) {
      return false;
    }

    if (
      config.OFFER_50_PERCENT_DISCOUNT_ENABLED &&
      selectedReasonId === CANCEL_REASON_TOO_EXPENSIVE &&
      !billingInfo.subscription50PercentOffUsed &&
      !is50PercentDismissed
    ) {
      if (billingInfo.subscriptionLevel === SUBSCRIPTION_LEVELS.PREMIUM) {
        const isDiscountAccepted = await grant50PercentModalState.getResult();

        if (isDiscountAccepted) {
          closeModal();
        } else {
          setIs50PercentDismissed(true);
        }
      }

      if (billingInfo.subscriptionLevel === SUBSCRIPTION_LEVELS.BUSINESS) {
        const isDowngradeAccepted = await maybeDowngrade();
        if (isDowngradeAccepted) {
          closeModal();
        }
      }
      return true;
    }

    if (
      selectedReasonId === CANCEL_REASON_SEASONAL &&
      !billingInfo.pausedDate &&
      !isPauseOfferDismissed
    ) {
      const isPaused = await maybePauseSubscription();

      if (isPaused) {
        closeModal();
      } else {
        setIsPauseOfferDismissed(true);
      }
      return true;
    }

    return false;
  };

  const maybeDowngrade = () => {
    return downgradeOfferModalState.getResult();
  };

  const maybePauseSubscription = () => {
    return pauseSubscriptionModalState.getResult();
  };

  const onCancelSubscription = async () => {
    const isOfferNotDeclined = !is50PercentDismissed && !isPauseOfferDismissed;
    if (isOfferNotDeclined && (await limitChurn())) {
      return;
    }

    await tryCancelSubscription();
  };

  const tryCancelSubscription = async () => {
    if (await limitChurn()) {
      return;
    }

    // if an org purchased within last 30 days, we don't offer discount or pause
    if (
      selectedReasonId === CANCEL_REASON_TOO_EXPENSIVE &&
      !is50PercentDismissed &&
      didPurchaseOver30DaysAgo
    ) {
      return;
    }

    if (
      selectedReasonId === CANCEL_REASON_SEASONAL &&
      !isPauseOfferDismissed &&
      didPurchaseOver30DaysAgo
    ) {
      return;
    }

    if (
      selectedReasonId === CANCEL_REASON_TOO_EXPENSIVE &&
      billingInfo.subscriptionLevel === SUBSCRIPTION_LEVELS.BUSINESS &&
      (await maybeDowngrade())
    ) {
      return;
    }

    try {
      const { data } = await api.account.cancelSubscription({
        terminationOption,
        reasonId: selectedReasonId,
        reasonText: selectedReasonText,
      });
      onBillingInfoUpdated(extendBillingInfo(data));
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      throw e;
    }
  };

  const isFormValid =
    showFeaturesToLose || (selectedReasonId && !(reasonRequiresText && !selectedReasonText));

  const onSubmit = async () => {
    if (showFeaturesToLose) {
      return void setShowFeaturesToLose(false);
    }

    setIsSubmitted(true);
    if (!isFormValid) {
      throw 'form invalid';
    }

    if (isDowngradeOnly) {
      await onDowngradeSubscription();
      closeModal();
    } else {
      await onCancelSubscription();
    }
  };

  return {
    showFeaturesToLose,
    setShowFeaturesToLose,
    isDowngradeOnly,
    setIsDowngradeOnly,
    selectedReasonId,
    setSelectedReasonId,
    selectedReasonText,
    setSelectedReasonText,
    setReasonRequiresText,
    reasonRequiresText,
    terminationOption,
    setTerminationOption,
    grant50PercentModalState,
    downgradeOfferModalState,
    pauseSubscriptionModalState,
    onSubmit,
    creditCardModalState,
    isFormValid,
    isSubmitted,
    setIsSubmitted,
    setIs50PercentDismissed,
    setIsPauseOfferDismissed,
  };
};

export default useDowngradeSubscription;
